import React from 'react';
import { ArrowUpRight, Sparkles, Zap } from 'lucide-react';
import { Button } from './ui/button';
import BackgroundFX from './BackgroundFX';
import SectionLabel from './SectionLabel';
import { markets, CTA_URL } from '../mock';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';

export default function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative pt-36 pb-28 md:pt-44 md:pb-32 overflow-hidden">
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          className="absolute inset-0 w-full h-full object-cover object-center"
          autoPlay loop muted defaultMuted playsInline webkit-playsinline="true" preload="auto" disableRemotePlayback
          aria-hidden="true"
        >
          <source src="/hero.webm" type="video/webm" />
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#ffffff]/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#ffffff]/70 via-[#ffffff]/15 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#ffffff]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.10),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(14,165,233,0.08),transparent_65%)]" />
        <div className="absolute inset-0 mix-blend-overlay bg-sky-500/10" />
        <div className="absolute inset-0 grid-lines opacity-15" />
      </div>

      <BackgroundFX />

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 space-y-8">
            <SectionLabel>{t('hero.tag')}</SectionLabel>
            <h1 className="font-display text-[42px] leading-[1.05] sm:text-[56px] md:text-[68px] lg:text-[76px] font-semibold tracking-tight text-slate-900">
              {t('hero.title_a')}<br />
              {t('hero.title_b')}<br />
              <span className="text-gradient-green glow-text">{t('hero.title_c')}</span>
            </h1>
            <p className="max-w-2xl text-base md:text-lg text-slate-700/70 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a href={CTA_URL} target="_blank" rel="noreferrer">
                <Button className="btn-primary bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl px-6 h-12 text-[15px]">
                  {t('common.free_consultation')}<ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Button>
              </a>
              <Link to="/services">
                <Button variant="outline" className="rounded-xl h-12 px-6 text-[15px] bg-transparent border-blue-500/25 text-slate-700 hover:bg-blue-500/10 hover:text-slate-900 hover:border-blue-500/50">
                  {t('common.our_services')}
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-6 text-xs text-slate-500">
              <div className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-blue-600" />{t('hero.trust_1')}</div>
              <div className="flex items-center gap-2"><Zap className="h-3.5 w-3.5 text-blue-600" />{t('hero.trust_2')}</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{t('hero.trust_3')}</div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/20 via-sky-500/5 to-transparent blur-2xl" />
              <div className="relative glass-strong rounded-3xl p-6 space-y-5">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-blue-600">
                  {t('hero.markets_title')}<span className="h-1.5 w-1.5 rounded-full bg-blue-500 pulse-ring relative" />
                </div>
                <div className="space-y-3">
                  {markets.map((m) => (
                    <div key={m.code} className="group flex items-center justify-between rounded-xl border border-blue-500/10 bg-slate-50 px-4 py-3.5 hover:border-blue-500/30 hover:bg-blue-50 transition-all">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{m.flag}</span>
                        <span className="font-mono text-[11px] tracking-widest text-blue-600">{m.code}</span>
                      </div>
                      <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">{t(`markets.${m.code}`)}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-blue-500/10">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="font-display text-3xl font-semibold text-slate-900">{t('hero.markets_stat')}</div>
                      <div className="text-xs text-slate-500">{t('hero.markets_stat_label')}</div>
                    </div>
                    <span className="text-blue-600 text-xs tracking-widest">{t('common.live')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
